

import { getPublicData } from "../../../../../services/publicPegasApi/MakePublicOrder";
import { getSubdomain } from "../../../../../services/helpers";

// ✅ تحويل صورة إلى Base64 (أفضل حل للطباعة)
const toBase64 = async (url) => {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error("Failed to fetch image for base64");
  const blob = await res.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => resolve(reader.result); // data:image/...;base64,...
    reader.readAsDataURL(blob);
  });
};

// helpers
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const isPercent = (v) => Number(v) === 1 || v === true || v === "1";

// ✅ يحسب قيمة بند (قيمة ثابتة + نسبة من subtotal لو is_percentage)
const calcValue = ({
  value,
  ratio,
  is_percentage,
  subtotal,
}) => {
  const val = toNum(value);
  const r = toNum(ratio);
  if (isPercent(is_percentage) || r > 0) {
    return (subtotal * r) / 100;
  }
  return val;
};

export default async function CustomerInvoiceTemplate(
  orderDetails = {},
  invoiceData = {},
  orderId
) {
  // ✅ Normalize: لو جالك wrapper {order, items, invoiceSettings} خده صح
  const od = orderDetails?.order ? orderDetails.order : orderDetails;
  const items = orderDetails?.items || od?.items || [];

  // ✅ order id fallback
  const safeOrderId = orderId ?? od?.id ?? od?.order_id ?? "-";
  const safeGeneralIncrement = od?.general_increment ?? safeOrderId;

  let publicData = {};
  try {
    publicData = await getPublicData();
    console.log("📞 بيانات الشركة المسترجعة:", publicData);
  } catch (err) {
    console.error("❌ فشل في جلب البيانات العامة:", err);
  }

  // ✅ رابط كامل + ترميز المسارات (بدون حذف المسافات)
  const subdomain = getSubdomain();
  const base = `https://${subdomain}.restocash.app/`;

  const logoPath = (publicData?.logo || "").trim();
  const logoUrl = logoPath ? new URL(encodeURI(logoPath), base).toString() : "";
  console.log("🖼️ رابط اللوجو النهائي:", logoUrl);

  // ✅ Base64 للّوجو (الأفضل للطباعة)
  let logoBase64 = "";
  if (logoUrl) {
    try {
      logoBase64 = await toBase64(logoUrl);
      console.log("✅ Logo Base64 جاهز للطباعة");
    } catch (e) {
      console.warn("⚠️ فشل تحويل اللوجو Base64، هنستخدم URL عادي", e);
    }
  }

  // ✅ subtotal قوي + آمن
  const subtotal = items.reduce((acc, item) => {
    const qty = toNum(item.quantity);
    const unit =
      toNum(item.price) ||
      toNum(item.unit_price) ||
      (toNum(item.total) && qty ? toNum(item.total) / qty : 0);
    return acc + unit * qty;
  }, 0);

  // ✅ service/tax/discount سواء value أو ratio (%)
  const service = calcValue({
    value: invoiceData.service,
    ratio: invoiceData.service_ratio,
    is_percentage: invoiceData.service_is_percentage,
    subtotal,
  });

  const tax = calcValue({
    value: invoiceData.tax,
    ratio: invoiceData.tax_ratio,
    is_percentage: invoiceData.tax_is_percentage,
    subtotal,
  });

  const discount = calcValue({
    value: invoiceData.discount,
    ratio: invoiceData.discount_ratio,
    is_percentage: invoiceData.discount_is_percentage,
    subtotal,
  });

  // ✅ total
  const total = subtotal + service + tax - discount;

  const footerText = (() => {
    const baseTxt = (invoiceData.footer_text || "").trim();
    const note = (publicData?.invoice_note_printer || "").trim();
    const pieces = [];
    if (baseTxt) pieces.push(baseTxt);
    if (note) pieces.push(note);
    return pieces.join("<br/>");
  })();

  const address = publicData?.address || "";
  const phone = publicData?.phone || "";

  const orderPrintNumber = invoiceData?.order_print_number || 1;

  // ✅ مصدر الصورة: Base64 لو متاح، وإلا URL
  const logoSrc = logoBase64 || logoUrl;

  // ✅ بيانات العميل/الطاولة بشكل آمن
  const customerName = od?.customer?.name || "بدون اسم";
  const tableName = od?.table?.name || "غير محددة";

  return `
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <meta charset="UTF-8" />
      ${logoUrl ? `<link rel="preload" as="image" href="${logoUrl}" />` : ""}

      <style>
        @page { size: 80mm auto; margin: 0; }
        html, body {
          height: 100%;
          margin: 0;
          padding: 0;
          display: flex;
          justify-content: center;
          background: #fff;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .receipt {
          font-family: 'Cairo', sans-serif;
          width: 80mm;
          padding: 10px;
          margin: 0 auto;
          font-size: 14px;
          font-weight: bold;
          color: #000;
          direction: rtl;
          text-align: right;
          box-sizing: border-box;
          line-height: 1.6;
          text-shadow: 0.3px 0.3px 0px rgba(0, 0, 0, 0.5);
        }
        h2, h3 { margin: 6px 0; text-align: center; font-weight: 900; text-shadow: 0.5px 0.5px 0px rgba(0, 0, 0, 0.6); line-height: 1.5; }
        hr { border: none; border-top: 1.5px dashed #000; margin: 8px 0; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 5px 0; text-align: right; font-weight: bold; line-height: 1.5; }
        th { border-bottom: 1.5px dashed #000; text-shadow: 0.3px 0.3px 0px rgba(0, 0, 0, 0.5); }
        .footer { text-align: center; margin-top: 12px; font-size: 12px; font-weight: bold; line-height: 1.6; }
        .logo {
          width: 120px;
          height: auto;
          object-fit: contain;
          display: block;
          margin: 0 auto 20px;
        }
      </style>

      <script>
        async function waitForAssets() {
          try {
            if (document.fonts && document.fonts.ready) {
              await document.fonts.ready;
            }
          } catch (e) {}

          const imgs = Array.from(document.images || []);
          await Promise.all(imgs.map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise(resolve => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            });
          }));

          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        }

        window.__waitForAssets = waitForAssets;
      </script>
    </head>

    <body>
      <div class="receipt">
        ${logoSrc ? `<img src="${logoSrc}" class="logo" alt="Logo" onerror="this.style.display='none'" />` : ""}

        <h2>${invoiceData.header_text || publicData?.name || "تطبيق رستو كاش"}</h2>
        <h3> رقم #${safeGeneralIncrement}</h3>

        <hr />
        <p>العميل: ${customerName}</p>
        <p>الطاولة: ${tableName}</p>

        <hr />
        <table>
          <thead>
            <tr>
              <th>الصنف</th>
              <th>السعر</th>
              <th>الكمية</th>
              <th>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            ${items
              .map((item) => {
                const qty = toNum(item.quantity);
                const unit =
                  toNum(item.price) ||
                  toNum(item.unit_price) ||
                  (toNum(item.total) && qty ? toNum(item.total) / qty : 0);
                const lineTotal = toNum(item.total) || unit * qty;

                return `
                  <tr>
                    <td>${item.product_name || "-"}</td>
                    <td>${unit.toFixed(2)} ج</td>
                    <td>${qty || 0}</td>
                    <td>${lineTotal.toFixed(2)} ج</td>
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>

        <hr />
        <p>الإجمالي الفرعي: ${subtotal.toFixed(2)} ج</p>
        <p>الخدمة: ${service.toFixed(2)} ج</p>
        <p>الضريبة: ${tax.toFixed(2)} ج</p>
        <p>الخصم: ${discount.toFixed(2)} ج</p>
        <hr />
        <h3>الإجمالي الكلي: ${total.toFixed(2)} ج</h3>
        <hr />

        <div class="footer">
          ${footerText ? `<p>${footerText}</p>` : `<p>شكرًا لزيارتكم ❤️</p>`}
          ${address ? `<p>📍 العنوان: ${address}</p>` : ""}
          ${phone ? `<p>📞 الهاتف: ${phone}</p>` : ""}
          <p>تم بواسطة نظام ريستو كاش</p>
        </div>
      </div>
    </body>
    </html>
  `;
}
